import React from 'react'
import Table, { ColumnsType, TableProps } from 'antd/es/table'
import { horsesApi } from 'api/horses'
import { PageWrapper, MainTitle } from 'components/ui'
import { EHorseStatus, IPopulatedHorseWithoutCount, TStatusOrder } from 'models/horse'
import { showServerError } from 'shared/utils/showServerError'
import { Button, message, Modal, Space, Tag, Typography } from 'antd'
import { MenuInfo } from 'rc-menu/lib/interface'
import moment from 'moment'
import { phoneMaskStatic } from 'shared/utils/phoneMaskStatic'
import { StatusDropdown } from 'components/ui/StatusDropdown/StatusDropdown'
import { CardLink } from 'components/ui/CardLink/CardLink'
import { getUserEditPath } from 'routing/utils/getUserEditPath'
import { getHorseStatusItems, getHorseStatusView, horseStatusFilters, horseStatuses } from 'shared/constants/horses'
import { DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { bodyScrollLock } from 'shared/utils/bodyScrollLock'
import { useSearchParams } from 'react-router-dom'
import { cleanObject } from 'shared/utils/cleanObject'
import qs from 'qs'
import { RejectHorseModal } from 'components/horses/RejectHorseModal/RejectHorseModal'
import { getHorseDaysLeft } from 'shared/utils/getHorseDaysLeft'
import { useAppDispatch } from 'shared/hooks/useReduxHooks'
import { getModerationHorsesCount } from 'store/horses'

// клик по чекбоксу выделения и по выпадашке статуса не должен открывать карточку
const INTERACTIVE_CELL_SELECTOR = '.ant-table-selection-column, .ant-dropdown-trigger'

export const Horses: React.FC = () => {
  const [, setSearchParams] = useSearchParams()
  const dispatch = useAppDispatch()

  const [horses, setHorses] = React.useState<IPopulatedHorseWithoutCount[]>([])
  const [isLoading, setIsLoading] = React.useState(false)

  const [selectedItemsId, setSelectedItemsId] = React.useState<string[]>([])
  const [selectedHorseId, setSelectedHorseId] = React.useState<string | null>(null)
  const [openRejectedHorseModal, setOpenRejectedHorseModal] = React.useState(false)

  const [currentPage, setCurrentPage] = React.useState(1)
  const [total, setTotal] = React.useState(0)
  const [statuses, setStatuses] = React.useState<EHorseStatus[]>([])
  const [statusOrder, setStatusOrder] = React.useState<TStatusOrder>(undefined)
  const pageSize = 10

  const getHorses = async (skip: number, statusFilter = statuses, order = statusOrder) => {
    try {
      setIsLoading(true)
      const response = await horsesApi.getHorses({
        skip: String(skip),
        limit: String(pageSize),
        statuses: statusFilter,
        statusOrder: order
      })
      setTotal(response.data.count)
      setHorses(response.data.horses)
      setSearchParams(cleanObject({
        page: skip / pageSize + 1 === 1 ? '' : String(skip / pageSize + 1),
        status: statusFilter.join(','),
        sort: order ?? ''
      }))
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }

  React.useEffect(() => {
    const params = qs.parse(window.location.search.substring(1))
    const page = Number(params.page) || 1
    const initialStatuses = String(params.status ?? '')
      .split(',')
      .filter((item): item is EHorseStatus => item in horseStatuses)
    const initialOrder = params.sort === 'asc' || params.sort === 'desc' ? params.sort : undefined

    setCurrentPage(page)
    setStatuses(initialStatuses)
    setStatusOrder(initialOrder)
    getHorses((page - 1) * pageSize, initialStatuses, initialOrder)
  }, [])

  const onCloseRejectedHorseModal = React.useCallback(() => {
    setOpenRejectedHorseModal(false)
    bodyScrollLock.disable()
  }, [])

  const onOpenRejectedHorseModal = React.useCallback(() => {
    setOpenRejectedHorseModal(true)
    bodyScrollLock.enable()
  }, [])


  const onChangeHorseStatus = async (status: EHorseStatus, horseId?: string, rejectReason?: string) => {
    try {
      setIsLoading(true)
      const normalizedHorseId = horseId ?? selectedHorseId ?? ''
      await horsesApi.changeHorseStatus(normalizedHorseId, { status, reason_of_cancel: rejectReason })
      dispatch(getModerationHorsesCount())
      setSelectedHorseId(null)
      // перечитываем страницу: при активном фильтре лошадь могла из него выпасть
      await getHorses((currentPage - 1) * pageSize)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }

  const changeToRejectedStatus = (rejectReason: string) => onChangeHorseStatus(EHorseStatus.REJECTED, undefined, rejectReason)

  const onStatusTagClick = (horseId: string, e: MenuInfo) => {
    if (e.key === EHorseStatus.REJECTED) {
      setSelectedHorseId(horseId)
      return onOpenRejectedHorseModal()
    }
    onChangeHorseStatus(e.key as EHorseStatus, horseId)
  }

  const setProductsAction = async () => {
    try {
      await horsesApi.setActions({
        toDelete: selectedItemsId.join(',')
      })
      message.success(selectedItemsId.length === 1 ? 'Лошадь успешно удалена' : 'Выбранные лошади успешно удалены')
      dispatch(getModerationHorsesCount())
      if (selectedItemsId.length === horses.length) {
        getHorses(0)
        setCurrentPage(1)
      } else {
        getHorses((currentPage - 1) * pageSize)
      }
      setSelectedItemsId([])
    } catch (e) {
      showServerError(e)
    } finally {
      bodyScrollLock.disable()
    }
  }

  const showDeleteConfirm = () => {
    bodyScrollLock.enable()
    Modal.confirm({
      centered: true,
      title: 'Вы действительно хотите удалить выбранные лошади?',
      content: 'Отменить данное действие будет невозможно.',
      icon: <ExclamationCircleOutlined />,
      okText: 'Удалить',
      cancelText: 'Закрыть',
      onOk() {
        return new Promise((resolve) => {
          setProductsAction().then(resolve)
        })
      },
      onCancel() {
        bodyScrollLock.disable()
      }
    })
  }

  const onTableChange: TableProps<IPopulatedHorseWithoutCount>['onChange'] = (
    pagination,
    filters,
    sort
  ) => {
    const sorter = Array.isArray(sort) ? sort[0] : sort
    const nextOrder: TStatusOrder = sorter?.order === 'ascend'
      ? 'asc'
      : sorter?.order === 'descend'
        ? 'desc'
        : undefined
    const nextStatuses = (filters.status ?? []) as EHorseStatus[]
    // при смене фильтра или сортировки текущая страница может уже не существовать — возвращаемся на первую
    const isQueryChanged = nextStatuses.join(',') !== statuses.join(',') || nextOrder !== statusOrder
    const page = isQueryChanged ? 1 : pagination.current ?? 1

    setStatuses(nextStatuses)
    setStatusOrder(nextOrder)
    setCurrentPage(page)
    getHorses((page - 1) * pageSize, nextStatuses, nextOrder)
  }

  const openHorse = (event: React.MouseEvent<HTMLElement>, horseId: string) => {
    if ((event.target as HTMLElement).closest(INTERACTIVE_CELL_SELECTOR)) return
    window.open(`/horses/${horseId}`, '_blank', 'noreferrer')
  }

  const onRowSelection = (selectedItemsId: React.Key[]) => {
    setSelectedItemsId(selectedItemsId as string[])
  }

  const columns: ColumnsType<IPopulatedHorseWithoutCount> = React.useMemo(
    () => [
      {
        title: 'Номер',
        dataIndex: 'number',
        width: '10%'
      },
      {
        title: 'Дата',
        dataIndex: 'date',
        width: '13%',
        render: (_, { createdAt }) => {
          return <Typography.Text>{moment(createdAt).format('DD.MM.YYYY')}</Typography.Text>
        }
      },
      {
        title: 'Продавец',
        dataIndex: 'seller',
        width: '21%',
        render: (_, { user_id }) => {
          return user_id?._id ? (
            <CardLink to={getUserEditPath(user_id._id)}>
              {user_id.full_name || 'ФИО не заполнено'}
            </CardLink>
          ) : (
            <Typography.Text style={{ opacity: 0.5 }}>Удаленный аккаунт</Typography.Text>
          )
        }
      },
      {
        title: 'Телефон',
        dataIndex: 'sellerPhone',
        width: '18%',
        render: (_, { user_id }) => {
          return (
            <Typography.Text style={{ opacity: user_id?.tel ? 1 : 0.5 }}>{user_id ? user_id?.tel ? phoneMaskStatic(user_id.tel) : 'Номер телефона не заполнен' : 'Удаленный аккаунт'}</Typography.Text>
          )
        }
      },
      {
        title: 'Лошадь',
        dataIndex: 'name',
        width: '17%'
      },
      {
        title: 'Статус',
        dataIndex: 'status',
        width: '21%',
        filters: horseStatusFilters,
        filteredValue: statuses.length ? statuses : null,
        sorter: true,
        sortOrder: statusOrder === 'asc' ? 'ascend' : statusOrder === 'desc' ? 'descend' : undefined,
        render: (_, { _id, status, published_until, archived_reason }) => {
          const daysLeft = getHorseDaysLeft(published_until)
          const view = getHorseStatusView({ status, archived_reason })
          return (
            <Space direction='vertical' size={4} align='start'>
              <StatusDropdown
                items={getHorseStatusItems(status)}
                label={view.label}
                color={view.color}
                onClick={(e) => onStatusTagClick(_id, e)}
              />
              {view.note && <Tag color='orange'>{view.note}</Tag>}
              {status === EHorseStatus.PUBLISHED && daysLeft !== null && (
                <Tag color={daysLeft > 7 ? 'blue' : 'orange'}>
                  Осталось дней: {daysLeft}
                </Tag>
              )}
            </Space>
          )
        }
      }
    ],
    [statuses, statusOrder]
  )

  return (
    <>
      <PageWrapper>
        <MainTitle text='Лошади' />
        <Table
          columns={columns}
          dataSource={horses}
          loading={isLoading}
          rowKey={(el) => el._id}
          showSorterTooltip={false}
          onChange={onTableChange}
          rowSelection={{
            selectedRowKeys: selectedItemsId,
            onChange: onRowSelection
          }}
          onRow={({ _id }) => ({
            onClick: (e) => openHorse(e, _id),
            style: { cursor: 'pointer' }
          })}
          rowClassName='tableRow'
          footer={() => (
            <Space size={8}>
              <Typography.Text>Действия:</Typography.Text>
              <Button
                danger
                type='primary'
                icon={<DeleteOutlined />}
                onClick={showDeleteConfirm}
                disabled={!selectedItemsId.length}
              >
                Удалить
              </Button>
            </Space>
          )}
          pagination={{
            current: currentPage,
            pageSize,
            total,
            showSizeChanger: false
          }}
        />
      </PageWrapper>
      <RejectHorseModal
        isOpen={openRejectedHorseModal}
        onSuccess={changeToRejectedStatus}
        close={onCloseRejectedHorseModal}
      />
    </>
  )
}